<template>
  <div class="leecho-page">
    <section class="hero">
      <div class="hero-copy">
        <span class="eyebrow">AI SMART GLASSES COMPANION</span>
        <h1>Le-Echo</h1>
        <h2>{{ $t('leecho.subtitle') }}</h2>
        <p>{{ $t('leecho.description') }}</p>
        <div class="hero-actions">
          <a href="#download" class="primary-action">{{ $t('leecho.download.title') }}</a>
          <a href="#features" class="secondary-action">{{ featureAction }}</a>
        </div>
      </div>
      <div class="hero-visual">
        <img :src="heroImage" :alt="$t('leecho.title')">
      </div>
    </section>

    <section id="features" class="features-section">
      <div class="section-heading">
        <span>{{ featureKicker }}</span>
        <h2>{{ featureTitle }}</h2>
        <p>{{ featureIntro }}</p>
      </div>
      <div class="feature-grid">
        <article v-for="feature in features" :key="feature.title" class="feature-card">
          <div class="feature-image">
            <img :src="feature.image" :alt="feature.title">
          </div>
          <div class="feature-copy">
            <span class="feature-number">0{{ feature.number }}</span>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </div>
        </article>
      </div>
    </section>

    <section id="download" class="download-section">
      <div class="download-copy">
        <span>{{ downloadKicker }}</span>
        <h2>{{ $t('leecho.download.title') }}</h2>
        <p>{{ downloadDescription }}</p>
      </div>
      <div class="download-buttons">
        <a :href="iosDownloadUrl" target="_blank" rel="noopener noreferrer" class="download-button">
          <img :src="appleIcon" alt="iOS">
          <div>
            <small>{{ $t('leecho.download.ios_label') }}</small>
            <strong>{{ $t('leecho.download.ios_text') }}</strong>
          </div>
          <span class="arrow">↗</span>
        </a>
        <a :href="androidDownloadUrl" target="_blank" rel="noopener noreferrer" class="download-button">
          <img :src="androidIcon" alt="Android">
          <div>
            <small>{{ $t('leecho.download.android_label') }}</small>
            <strong>{{ $t('leecho.download.android_text') }}</strong>
          </div>
          <span class="arrow">↗</span>
        </a>
      </div>
    </section>

    <section class="legal-section">
      <p>{{ $t('leecho.agreement.prefix') }}</p>
      <div class="legal-links">
        <a :href="userAgreementUrl" target="_blank" rel="noopener noreferrer">{{ $t('leecho.agreement.user') }}</a>
        <span></span>
        <a :href="privacyPolicyUrl" target="_blank" rel="noopener noreferrer">{{ $t('leecho.agreement.privacy') }}</a>
      </div>
    </section>
  </div>
</template>

<script>
const featureImages = [
  require('@/assets/images/leecho/voice-control.webp'),
  require('@/assets/images/leecho/visual-analysis.webp'),
  require('@/assets/images/leecho/context-aware.webp')
];

export default {
  name: 'LeEcho',
  data() {
    return {
      iosDownloadUrl: 'https://www.pgyer.com/leecho-ios',
      androidDownloadUrl: 'https://www.pgyer.com/leecho-android-n',
      appleIcon: require('@/assets/images/phone/apple.png'),
      androidIcon: require('@/assets/images/phone/android.png'),
      heroImage: require('@/assets/images/leecho/hero.webp')
    };
  },
  computed: {
    isEnglish() {
      return this.$i18n.locale.indexOf('en') !== -1;
    },
    features() {
      return [1, 2, 3].map((number, index) => ({
        number,
        title: this.$t(`leecho.feature${number}.title`),
        description: this.$t(`leecho.feature${number}.description`),
        image: featureImages[index]
      }));
    },
    featureAction() {
      return this.isEnglish ? 'Explore features' : '了解核心能力';
    },
    featureKicker() {
      return this.isEnglish ? 'CORE CAPABILITIES' : '核心能力';
    },
    featureTitle() {
      return this.isEnglish ? 'Intelligence that responds naturally' : '让智能，自然回应每个当下';
    },
    featureIntro() {
      return this.isEnglish
        ? 'Voice, vision, and contextual awareness work together to create an effortless smart-glasses experience.'
        : '让语音、视觉与情境感知协同工作，为智能眼镜带来更自然、更贴合场景的使用体验。';
    },
    downloadKicker() {
      return this.isEnglish ? 'GET LE-ECHO' : '获取 LE-ECHO';
    },
    downloadDescription() {
      return this.isEnglish
        ? 'Connect your smart glasses and begin a more intuitive digital experience.'
        : '连接智能眼镜，即刻开启更自然、更懂你的数字体验。';
    },
    privacyPolicyUrl() {
      return this.isEnglish
        ? 'https://le-echo.mltcloud.com/private/privacy_policy_en.html'
        : 'https://le-echo.mltcloud.com/private/privacy_policy.html';
    },
    userAgreementUrl() {
      return this.isEnglish
        ? 'https://le-echo.mltcloud.com/private/user_agreement_en.html'
        : 'https://le-echo.mltcloud.com/private/user_agreement.html';
    }
  },
  mounted() {
    document.body.className = 'page-template-default page page-one-column colors-light';
  }
};
</script>

<style lang="stylus" scoped>
.leecho-page
  color #eef3ff
  background #050817
  overflow hidden

.hero
  min-height 680px
  display grid
  grid-template-columns minmax(360px, 0.82fr) minmax(560px, 1.35fr)
  align-items center
  max-width 1480px
  margin 0 auto
  padding 76px 48px 90px
  gap 58px
  position relative

  &::before
    content ''
    position absolute
    width 500px
    height 500px
    border-radius 50%
    background rgba(87, 74, 255, 0.16)
    filter blur(100px)
    left -260px
    top 60px

.hero-copy
  position relative
  z-index 2

  .eyebrow
    display inline-block
    color #7ce8ff
    font-size 12px
    font-weight 700
    letter-spacing .19em
    margin-bottom 22px

  h1
    margin 0
    font-size clamp(68px, 7vw, 108px)
    line-height .92
    letter-spacing -.07em
    background linear-gradient(110deg, #fff 4%, #76e8ff 46%, #9b7bff 90%)
    -webkit-background-clip text
    -webkit-text-fill-color transparent

  h2
    margin 32px 0 18px
    font-size clamp(25px, 2.5vw, 40px)
    line-height 1.3
    letter-spacing -.035em

  p
    max-width 590px
    margin 0
    color #aab7d7
    font-size 16px
    line-height 1.9

.hero-actions
  display flex
  flex-wrap wrap
  gap 14px
  margin-top 34px

  a
    min-width 148px
    padding 13px 22px
    border-radius 999px
    font-size 14px
    font-weight 600
    text-align center
    text-decoration none
    transition transform .25s ease, background .25s ease

    &:hover
      transform translateY(-2px)

  .primary-action
    color #07101d
    background linear-gradient(110deg, #72e7ff, #a78cff)

  .secondary-action
    color #dce7ff
    border 1px solid rgba(174, 194, 255, .28)
    background rgba(255, 255, 255, .05)

.hero-visual
  position relative
  border-radius 36px
  overflow hidden
  box-shadow 0 40px 100px rgba(0, 0, 0, .48)

  &::after
    content ''
    position absolute
    inset 0
    border 1px solid rgba(143, 215, 255, .22)
    border-radius inherit
    pointer-events none

  img
    display block
    width 100%
    aspect-ratio 1.58
    object-fit cover

.features-section
  padding 106px 48px 120px
  background linear-gradient(180deg, #080d22 0%, #f5f7ff 24%, #f5f7ff 100%)

.section-heading
  max-width 760px
  margin 0 auto 48px
  text-align center
  color #111b3f

  span
    display block
    margin-bottom 13px
    color #566bdd
    font-size 12px
    font-weight 700
    letter-spacing .18em

  h2
    margin 0 0 16px
    font-size clamp(34px, 4vw, 54px)
    line-height 1.16
    letter-spacing -.045em

  p
    margin 0
    color #687492
    font-size 16px
    line-height 1.8

.feature-grid
  display grid
  grid-template-columns repeat(3, minmax(0, 1fr))
  gap 24px
  max-width 1320px
  margin 0 auto

.feature-card
  min-width 0
  background #fff
  border 1px solid rgba(82, 102, 174, .12)
  border-radius 26px
  overflow hidden
  box-shadow 0 22px 60px rgba(28, 44, 105, .09)
  transition transform .3s ease, box-shadow .3s ease

  &:hover
    transform translateY(-7px)
    box-shadow 0 30px 70px rgba(28, 44, 105, .16)

.feature-image
  height 260px
  padding 12px
  background #e8ecff

  img
    display block
    width 100%
    height 100%
    object-fit contain
    border-radius 18px

.feature-copy
  min-height 220px
  padding 27px 28px 31px

  .feature-number
    color #7589e8
    font-size 12px
    font-weight 700
    letter-spacing .12em

  h3
    margin 10px 0 12px
    color #172141
    font-size 22px
    line-height 1.35

  p
    margin 0
    color #68738f
    font-size 15px
    line-height 1.75

.download-section
  max-width 1320px
  margin 0 auto
  padding 82px 70px
  display flex
  align-items center
  justify-content space-between
  gap 48px
  position relative

  &::before
    content ''
    position absolute
    width 320px
    height 320px
    right 10%
    top -120px
    border-radius 50%
    background rgba(61, 194, 255, .12)
    filter blur(80px)

.download-copy
  position relative
  max-width 520px

  > span
    color #71dcff
    font-size 12px
    font-weight 700
    letter-spacing .18em

  h2
    margin 12px 0
    font-size clamp(34px, 4vw, 52px)

  p
    margin 0
    color #aab7d7
    font-size 16px
    line-height 1.8

.download-buttons
  position relative
  display flex
  flex-direction column
  gap 13px
  width min(100%, 430px)

.download-button
  display grid
  grid-template-columns 46px 1fr auto
  align-items center
  gap 15px
  padding 17px 20px
  color #eef3ff
  text-decoration none
  border 1px solid rgba(166, 192, 255, .2)
  border-radius 18px
  background rgba(255, 255, 255, .07)
  backdrop-filter blur(16px)
  transition transform .25s ease, border-color .25s ease, background .25s ease

  &:hover
    transform translateX(5px)
    border-color rgba(113, 220, 255, .55)
    background rgba(255, 255, 255, .11)

  img
    width 40px
    height 40px
    object-fit contain

  div
    display flex
    flex-direction column
    gap 2px

  small
    color #9facca
    font-size 11px

  strong
    font-size 17px

  .arrow
    color #72e7ff
    font-size 20px

.legal-section
  padding 28px 24px 42px
  text-align center
  border-top 1px solid rgba(169, 192, 255, .1)

  p
    margin 0 0 9px
    color #76829f
    font-size 13px

.legal-links
  display flex
  justify-content center
  align-items center
  gap 13px

  span
    width 3px
    height 3px
    border-radius 50%
    background #4e5b7e

  a
    color #aebde0
    font-size 13px
    text-decoration none

    &:hover
      color #72e7ff

@media (max-width: 1024px)
  .hero
    min-height auto
    grid-template-columns 1fr
    padding 68px 32px 80px

  .hero-copy
    max-width 760px

  .hero-visual
    max-width 900px

  .feature-grid
    grid-template-columns repeat(2, minmax(0, 1fr))

  .feature-card:last-child
    grid-column 1 / -1
    max-width calc(50% - 12px)
    width 100%
    justify-self center

@media (max-width: 768px)
  .hero
    padding 52px 20px 64px
    gap 42px

  .hero-copy
    h1
      font-size 62px

    h2
      margin-top 25px

  .hero-visual
    border-radius 24px

    img
      aspect-ratio 1.3

  .features-section
    padding 80px 20px 86px

  .feature-grid
    grid-template-columns 1fr

  .feature-card:last-child
    grid-column auto
    max-width none

  .feature-image
    height 250px

  .feature-copy
    min-height 0

  .download-section
    padding 68px 20px
    flex-direction column
    align-items flex-start

  .download-buttons
    width 100%

@media (max-width: 480px)
  .hero-copy
    h1
      font-size 52px

    p
      font-size 15px

  .hero-actions
    a
      flex 1
      min-width 132px

  .hero-visual img
    aspect-ratio 1.05

  .feature-image
    height 215px

  .download-button
    grid-template-columns 42px 1fr auto
    padding 15px
</style>
